import { zonedDate } from '../../../../utils/date-time';
// Detail view rendered from the client's appointment card (sheet no celular).
import moment from 'moment';
import 'moment/locale/pt-br';
import { ReactNode, useState } from 'react';
import { Agendamento } from '../../Enum';
import { baseURL, urlFile } from '../../../../AxiosConfig';
import {
    formatBRL,
    getRelativeDayLabel,
    getScheduleStatusLabel,
    getScheduleTone,
    getScheduleTotal,
} from './helpers';
import './MeusAgendamentos.css';
import { Building2, CalendarPlus, CircleCheck, Copy, ExternalLink, MessageCircle, Navigation } from 'lucide-react';

moment.locale('pt-br');

const paymentStatusLabels: Record<string, string> = {
    PAID: 'Pago',
    PENDING: 'Pendente',
    EXPIRED: 'Expirado',
    CANCELED: 'Cancelado',
    REFUNDED: 'Estornado',
    FAILED: 'Falhou',
};

const Row = ({ label, children, stacked = false }: { label: ReactNode; children?: ReactNode; stacked?: boolean }) => (
    <div className={`sf-ma-detail-row ${stacked ? 'is-stacked' : ''}`}>
        <span className="sf-ma-detail-label">{label}</span>
        {children !== undefined && <span className="sf-ma-detail-value">{children}</span>}
    </div>
);

const Group = ({ title, footer, children }: { title?: string; footer?: ReactNode; children: ReactNode }) => (
    <section>
        {title && <div className="sf-ma-section-title"><h2>{title}</h2></div>}
        <div className="sf-ma-group">{children}</div>
        {footer && <p className="sf-ma-footnote">{footer}</p>}
    </section>
);

export default function ScheduleView({
    schedule,
    onConfirm,
    onCancel,
}: {
    schedule: Agendamento;
    onConfirm?: () => void;
    onCancel?: () => void;
}) {
    const [pixCopied, setPixCopied] = useState(false);
    const start = zonedDate(schedule.startAt, schedule.timezone);
    const end = zonedDate(schedule.endAt, schedule.timezone);
    const services = [schedule.service, ...(schedule.servicosExtras || [])].filter((item) => item?.name);
    const totalPrice = getScheduleTotal(schedule);
    const payment = schedule.payment;
    const tone = getScheduleTone(schedule.status);
    const address = [
        schedule.estab?.address?.street, schedule.estab?.address?.number, schedule.estab?.address?.complement,
        schedule.estab?.address?.neighborhood, schedule.estab?.address?.city, schedule.estab?.address?.state,
        schedule.estab?.address?.zipCode,
    ].filter(Boolean).join(', ');
    const estabWhatsapp = schedule.estab?.isPhoneNumberWhatsapp ? schedule.estab?.phoneNumber?.replace(/\D/g, '') : '';
    const attendantWhatsapp = schedule.attendant?.phoneNumber?.replace(/\D/g, '');
    const mapsUrl = schedule.estab?.address?.addressUrl;
    const pixPending = payment?.status === 'PENDING';

    const copyPixPayload = async () => {
        if (!payment?.pix?.payload) return;
        await navigator.clipboard?.writeText(payment.pix.payload);
        setPixCopied(true);
        window.setTimeout(() => setPixCopied(false), 2000);
    };

    return (
        <div className="sf-ma-detail mx-auto w-full max-w-xl pb-2">
            <div className="sf-ma-hero">
                <div className="sf-ma-hero-main">
                    <div className="sf-ma-hero-eyebrow">
                        <span>{getRelativeDayLabel(schedule.startAt, undefined, schedule.timezone)}</span>
                        <span className="sf-ma-tag" data-tone={tone}>{getScheduleStatusLabel(schedule)}</span>
                    </div>
                    <div className="sf-ma-hero-time">
                        {start.format('HH:mm')} <span>–</span> {end.format('HH:mm')}
                    </div>
                    <div className="sf-ma-hero-date">
                        {start.format(start.year() === zonedDate(new Date(), schedule.timezone).year() ? 'dddd, D [de] MMMM' : 'dddd, D [de] MMMM [de] YYYY')}
                    </div>
                    <div className="sf-ma-hero-service">
                        {services.map((item) => item.name).join(', ') || 'Serviço não informado'}
                    </div>
                </div>

                {(estabWhatsapp || mapsUrl || schedule.googleEventLink) && (
                    <div className="sf-ma-actions">
                        {estabWhatsapp && (
                            <a href={`https://wa.me/${estabWhatsapp}`} target="_blank" rel="noopener noreferrer" className="sf-ma-action is-whatsapp">
                                <MessageCircle aria-hidden="true" />
                                <span>WhatsApp</span>
                            </a>
                        )}
                        {mapsUrl && (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="sf-ma-action">
                                <Navigation aria-hidden="true" />
                                <span>Como chegar</span>
                            </a>
                        )}
                        {schedule.googleEventLink && (
                            <a href={schedule.googleEventLink} target="_blank" rel="noopener noreferrer" className="sf-ma-action">
                                <CalendarPlus aria-hidden="true" />
                                <span>Calendário</span>
                            </a>
                        )}
                    </div>
                )}
            </div>

            {onConfirm && (
                <div className="sf-ma-group">
                    <button type="button" className="sf-ma-button-row is-accent" onClick={onConfirm}>
                        Confirmar presença
                    </button>
                </div>
            )}

            {payment && (
                <Group
                    title="Pagamento"
                    footer={payment.expiresAt && pixPending
                        ? `Pague até ${zonedDate(payment.expiresAt, schedule.timezone).format('DD/MM [às] HH:mm')} para garantir seu horário.`
                        : undefined}
                >
                    <Row label="Pix">
                        <span className="sf-ma-tag" data-tone={payment.status === 'PAID' ? 'green' : pixPending ? 'orange' : 'gray'}>
                            {paymentStatusLabels[payment.status] || payment.status}
                        </span>
                    </Row>
                    <Row label="Valor"><span className="sf-ma-amount">{formatBRL(payment.amount)}</span></Row>
                    {pixPending && (payment.pix?.encodedImage || payment.pix?.payload) && (
                        <div className="sf-ma-pix">
                            {payment.pix?.encodedImage && (
                                <img alt="QR Code Pix" src={`data:image/png;base64,${payment.pix.encodedImage}`} />
                            )}
                            {payment.pix?.payload && (
                                <>
                                    <code>{payment.pix.payload}</code>
                                    <button type="button" className="sf-ma-pill" onClick={copyPixPayload}>
                                        {pixCopied ? <CircleCheck aria-hidden="true" /> : <Copy aria-hidden="true" />}
                                        {pixCopied ? 'Código copiado' : 'Copiar código Pix'}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                    {payment.transactionReceiptUrl && (
                        <Row label="Comprovante">
                            <a href={payment.transactionReceiptUrl} target="_blank" rel="noopener noreferrer" className="sf-ma-link">
                                Abrir <ExternalLink className="text-[10px]" aria-hidden="true" />
                            </a>
                        </Row>
                    )}
                </Group>
            )}

            <Group title="Detalhes">
                <Row label="Total">
                    <span className="sf-ma-amount">{totalPrice == null ? '—' : formatBRL(totalPrice)}</span>
                </Row>
                <Row label="Profissional">
                    <img
                        alt=""
                        src={`${baseURL}/users/profileImage/${schedule.attendant.uid}`}
                        className="sf-ma-avatar"
                        loading="lazy"
                    />
                    <span className="truncate">{schedule.attendant.name}</span>
                    {attendantWhatsapp && (
                        <a href={`https://wa.me/${attendantWhatsapp}`} target="_blank" rel="noopener noreferrer"
                            className="sf-ma-link" aria-label="Conversar com o profissional pelo WhatsApp">
                            <MessageCircle className="text-base text-success" aria-hidden="true" />
                        </a>
                    )}
                </Row>
                {schedule.observationClient && (
                    <Row label="Sua observação" stacked>{schedule.observationClient}</Row>
                )}
                {(schedule.confirmation?.date || schedule.confirmationDate) && (
                    <Row label="Confirmado">
                        {zonedDate(schedule.confirmation?.date || schedule.confirmationDate, schedule.timezone).format('DD/MM [às] HH:mm')}
                    </Row>
                )}
                {schedule.cancellation?.date && (
                    <Row label="Cancelado">{zonedDate(schedule.cancellation.date, schedule.timezone).format('DD/MM [às] HH:mm')}</Row>
                )}
            </Group>

            <Group title="Local" footer={address || undefined}>
                <Row
                    label={
                        <span className="flex items-center gap-3">
                            {schedule.estab?.imageId ? (
                                <img src={`${urlFile}/${schedule.estab.imageId}`} alt="" className="sf-ma-estab-logo" />
                            ) : (
                                <span className="sf-ma-estab-logo flex items-center justify-center text-base-content/40">
                                    <Building2 aria-hidden="true" />
                                </span>
                            )}
                            <span className="min-w-0">
                                <span className="block font-medium text-base-content">{schedule.estab?.name}</span>
                                {schedule.estab?.description && (
                                    <span className="block truncate text-xs text-base-content/55">{schedule.estab.description}</span>
                                )}
                            </span>
                        </span>
                    }
                />
            </Group>

            {schedule.assessment?.value && (
                <Group title="Sua avaliação">
                    <Row label={
                        <span className="rating rating-sm" aria-label={`${schedule.assessment.value} de 5 estrelas`}>
                            {[...Array(5)].map((_, index) => (
                                <input key={index} type="radio" name={`rating-${schedule._id}`} className="mask mask-star-2 bg-amber-400"
                                    disabled defaultChecked={index + 1 === schedule.assessment?.value} />
                            ))}
                        </span>
                    } />
                    {schedule.assessment.observation && <Row label={schedule.assessment.observation} stacked />}
                </Group>
            )}

            {onCancel && (
                <div className="sf-ma-group">
                    <button type="button" className="sf-ma-button-row is-destructive" onClick={onCancel}>
                        Cancelar agendamento
                    </button>
                </div>
            )}
        </div>
    );
}
